import { computed } from 'vue';
import { START_LOCATION, useRoute, useRouter, type RouteLocationRaw, type RouteMeta } from 'vue-router';
import { keepAnchorDefaultBehavior } from '~/utils/dom';

const isSameOriginPath = (to: RouteLocationRaw): boolean => {
    if (typeof to !== 'string') {
        return true;
    }

    return to.startsWith('/') && !to.startsWith('//');
};

const opensInCurrentWindow = (target?: string | null): boolean => {
    return !target || target === '_self';
};

export const useRouterNavigation = () => {
    const router = useRouter();
    const route = useRoute();

    const initialNavigationPending = (): boolean => router.currentRoute.value === START_LOCATION;

    const currentPath = computed((): string => {
        if (initialNavigationPending()) {
            return window.location.pathname;
        }

        return route.path;
    });

    const currentMeta = computed((): RouteMeta => {
        if (initialNavigationPending()) {
            return router.resolve(window.location.pathname).meta;
        }

        return route.meta;
    });

    const hasRouterView = computed((): boolean => route.matched.length > 0);

    const isRouterNavigable = (to: RouteLocationRaw, target?: string | null): boolean => {
        if (!hasRouterView.value || !opensInCurrentWindow(target) || !isSameOriginPath(to)) {
            return false;
        }

        return router.resolve(to).matched.length > 0;
    };

    const pushWithFullLoadFallback = (to: RouteLocationRaw): void => {
        Promise.resolve(router.push(to)).catch(() => window.location.assign(router.resolve(to).href));
    };

    const navigate = (to: RouteLocationRaw, event: MouseEvent | KeyboardEvent, target?: string | null): boolean => {
        if (event.defaultPrevented || keepAnchorDefaultBehavior(event) || !isRouterNavigable(to, target)) {
            return false;
        }

        event.preventDefault();
        pushWithFullLoadFallback(to);

        return true;
    };

    return {
        currentMeta,
        currentPath,
        navigate,
    };
};
